/**
 * Transform SitemapTreeData tree → TreeViewNode[]
 * for the @ddc-webplatform/cmsc-components TreeView.
 *
 * - Consumes flat `children` array (not `{ list: [] }`)
 * - pageAlias, pageId, and source come directly from the DTO (no map lookups)
 * - Sets `nonLocalPage` for the library tooltip when source is inherited/distributed
 */

import { SitemapTreeData, TreeViewNode } from 'models';
import { isExternalUrl } from './utils';

/**
 * Derive a stable, unique ID from a node's full path.
 * Falls back to a numeric suffix on the rare collision (duplicate paths).
 */
const deriveStableId = (fullPath: string, usedIds: Set<string>): string => {
	const base = fullPath || '/';
	if (!usedIds.has(base)) return base;
	let i = 1;
	while (usedIds.has(`${base}__${i}`)) i += 1;
	return `${base}__${i}`;
};

/**
 * Check if a page path is in the invalid-pages list.
 */
const isInvalidPage = (fullPath: string, invalidPages: string[] = []): boolean => {
	if (!invalidPages.length || !fullPath) return false;
	return invalidPages.some(invalid => invalid === fullPath);
};

interface TransformContext {
	invalidPages: string[];
	usedIds: Set<string>;
}

/**
 * Recursively transform a SitemapTreeData to a TreeViewNode.
 */
const transformNode = (
	node: SitemapTreeData,
	parentPath: string,
	ctx: TransformContext
): TreeViewNode | null => {
	if (!node) return null;

	// Build full path from parent + segment
	const segment = node.path || '';
	if (isExternalUrl(segment)) return null;
	let currentPath: string;
	if (segment) {
		currentPath = parentPath ? `${parentPath}/${segment}` : `/${segment}`;
	} else {
		currentPath = parentPath ? `${parentPath}/` : '/';
	}

	const nodeId = deriveStableId(currentPath, ctx.usedIds);
	ctx.usedIds.add(nodeId);

	const isNonLocal =
		node.source === 'inherited' || node.source === 'distributed';

	const treeNode: TreeViewNode = {
		id: nodeId,
		label: node.path || '',
		pagePath: currentPath,
		pageAlias: node.pageAlias,
		pageId: node.pageId,
		source: node.source,
		nonLocalPage: isNonLocal || undefined
	};

	// Warning flag for invalid pages
	if (isInvalidPage(currentPath, ctx.invalidPages)) {
		treeNode.warning = true;
	}

	// Recurse into flat children array. Filtering out external-URL children
	// (see isExternalUrl above) can leave an empty array — only assign
	// `children` when something survived, so every consumer's "isFolder"
	// check (`children && children.length > 0`) still matches "was actually
	// a directory" rather than miscounting an all-external-URL directory as
	// a leaf page.
	if (node.children && node.children.length > 0) {
		const children = node.children
			.map(child => transformNode(child, currentPath, ctx))
			.filter(Boolean) as TreeViewNode[];

		if (children.length > 0) {
			treeNode.children = children;

			// Propagate warning up
			if (!treeNode.warning && children.some(c => c.warning)) {
				treeNode.warning = true;
			}
		}
	}

	return treeNode;
};

/**
 * Transform a SitemapTreeData tree to TreeViewNode[] items.
 *
 * @param tree  - Root SitemapTreeData (or array of roots)
 * @param invalidPages - Paths that have an alias but no pageId
 * @returns Array of TreeViewNode items (root's children; root itself is skipped)
 */
export const transformSitemapToTreeView = (
	tree: SitemapTreeData | SitemapTreeData[] | null | undefined,
	invalidPages: string[] = []
): TreeViewNode[] => {
	if (!tree) return [];

	const ctx: TransformContext = {
		invalidPages,
		usedIds: new Set<string>()
	};

	// If tree is already an array, transform each root node
	if (Array.isArray(tree)) {
		return tree
			.map(node => transformNode(node, '', ctx))
			.filter(Boolean) as TreeViewNode[];
	}

	// Skip the root node and return its children directly
	const rootChildren = tree.children;
	if (rootChildren && rootChildren.length > 0) {
		return rootChildren
			.map(child => transformNode(child, '', ctx))
			.filter(Boolean) as TreeViewNode[];
	}

	return [];
};

export default transformSitemapToTreeView;
